import React, { useContext, useState } from 'react';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T } from '@tolgee/react';
import { MapIcon } from '../../assets/MapIcon';
import Flag from 'react-world-flags';
import Close from '../../assets/Close';
import { ClientStoreBreadcrumbsProps } from './ClientStoreBreadcrumbs.interface';
import { OrderContext } from '../../context/OrderContext/order.context';
import { useNavigate } from 'react-router-dom';
import ModalDeleteAddress from './components/ModalDeleteAddress';
import { cn } from '../../lib/utils';
import { formatDate } from '../../utils/time.util';

const ClientStoreBreadcrumbs: React.FC<ClientStoreBreadcrumbsProps> = ({
	currentGroup,
	currentCategory,
	categoryId,
	currentProduct,
	storeOnly = false,
	showDeliveryAddress = true,
	contract,
}) => {
	const language = localStorage.getItem('lang') || 'en';
	const { order, additional, setAdditional } = useContext(OrderContext);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const navigate = useNavigate();

	const hasLocation = Boolean(order?.location);
	const shouldShowAddressRow = showDeliveryAddress && hasLocation;

	const contractProductName =
		currentProduct?.translations?.find((locale) => locale.language === language)
			?.name ??
		contract?.lines?.[0]?.product?.translations?.find(
			(locale) => locale.language === language,
		)?.name ??
		contract?.lines?.[0]?.product?.name;

	const contractAddress =
		order?.deliveryAddress ||
		order?.location?.label ||
		order?.location?.address?.label ||
		'';

	const handleDontShowAnymore = () => {
		setAdditional((prev) => ({
			...prev,
			dontShowAnymore: true,
		}));
	};

	const BreadcrumbItem = ({
		text,
		isActive,
		onClick,
		isClickable = true,
		className = '',
	}: {
		text: React.ReactNode;
		isActive: boolean;
		onClick?: () => void;
		isClickable?: boolean;
		className?: string;
	}) => (
		<SypacText variant="body-regular-medium">
			<p
				className={cn(
					'text-xs sm:text-sm transition',
					isClickable ? 'hover:text-gray-80' : '',
					isActive ? 'text-cornflower-blue' : 'text-gray-40',
					className,
				)}
				onClick={isClickable ? onClick : undefined}
				style={{ cursor: isClickable ? 'pointer' : 'default' }}
			>
				{text}
			</p>
		</SypacText>
	);

	return (
		<nav className="w-full flex flex-col gap-3">
			<section className="h-[16px] flex gap-3 items-center whitespace-nowrap">
				{contract ? (
					<>
						<Flag
							className="object-cover border border-solid border-gray-10 rounded"
							code={contract.countryCode}
							width={20}
							height={14}
						/>
						<BreadcrumbItem
							text={
								<>
									<T keyName="clientSelectQuantity.contract">Contract</T>{' '}
									{contract.id}
								</>
							}
							isActive={false}
							isClickable={false}
							className="mt-[2px]"
						/>
						<p className="text-[10px] text-gray-22">/</p>
						<BreadcrumbItem
							text={contractProductName}
							isActive={false}
							isClickable={false}
							className="mt-[2px]"
						/>

						{shouldShowAddressRow && contractAddress && (
							<>
								<p className="text-[10px] text-gray-22">/</p>
								<BreadcrumbItem
									text={contractAddress}
									isActive={true}
									isClickable={false}
									className="mt-[2px]"
								/>
							</>
						)}
					</>
				) : (
					<BreadcrumbItem
						text={<T keyName="clientSelectQuantity.store">Store</T>}
						isActive={storeOnly ? true : !currentGroup}
						onClick={() => navigate('/store')}
					/>
				)}

				{!storeOnly && !contract && currentGroup && (
					<>
						<p className="text-[10px] text-gray-22">/</p>
						<BreadcrumbItem
							text={currentGroup.name}
							isActive={!currentCategory}
							onClick={() => currentCategory && navigate('/store/groups')}
							isClickable={Boolean(currentCategory)}
						/>
					</>
				)}

				{!storeOnly && !contract && currentCategory && (
					<>
						<p className="text-[10px] text-gray-22">/</p>
						<BreadcrumbItem
							text={currentCategory.name}
							isActive={!currentProduct}
							onClick={() =>
								categoryId &&
								navigate(
									`/store/groups/${currentGroup?.id}/categories/${categoryId}/products`,
								)
							}
							isClickable={Boolean(categoryId)}
						/>
					</>
				)}

				{!storeOnly && !contract && currentProduct && (
					<>
						<p className="text-[10px] text-gray-22">/</p>
						<BreadcrumbItem
							text={currentProduct.name}
							isActive={true}
							isClickable={false}
						/>
					</>
				)}

				{contract ? (
					<div className="flex mt-[2px] ml-auto">
						<SypacText variant="body-regular-medium">
							<p className="text-sm text-gray-40">
								<T keyName="clientSelectQuantity.contractCreated">
									Contract created:
								</T>
							</p>
						</SypacText>
						<SypacText variant="body-regular-medium">
							<p className="text-sm text-gray-40">
								{formatDate(contract.createdAt)}
							</p>
						</SypacText>
					</div>
				) : null}
			</section>

			{!contract && shouldShowAddressRow && (
				<>
					<section className="w-full flex flex-col lg:flex-row gap-4">
						<div className="flex gap-4 whitespace-nowrap">
							<span className="scale-[1.2]">
								<MapIcon green={true} />
							</span>
							<SypacText variant="body-regular-medium">
								<p className="text-sm text-gray-80 mt-[1px]">
									<T keyName="clientSelectQuantity.deliveryAddress">
										Delivery address:
									</T>
								</p>
							</SypacText>
						</div>

						<div className="w-full flex gap-4">
							<span className="mt-[2px]">
								<Flag
									className="object-cover border border-solid border-gray-10 rounded"
									code={order?.location?.countryCode?.toUpperCase()}
									width={20}
									height={14}
								/>
							</span>
							<SypacText variant="body-regular-medium">
								<p className="text-sm text-gray-80 mt-[1px]">
									{order?.deliveryAddress ||
										order?.location?.label ||
										order?.location?.address?.label}
								</p>
							</SypacText>

							<SypacButton
								variant="subTitle"
								className="ml-auto sm:ml-0 lg:ml-[4px]"
							>
								<button
									type="button"
									onClick={() =>
										additional.dontShowAnymore
											? navigate('/store')
											: setShowDeleteModal(true)
									}
									className="w-[22px] h-[22px] flex justify-center items-center p-0 bg-white border border-solid border-gray-10 rounded-md transition hover:border-gray-60"
								>
									<Close width="10" height="10" />
								</button>
							</SypacButton>
						</div>
					</section>

					<ModalDeleteAddress
						isOpen={showDeleteModal}
						onClose={() => setShowDeleteModal(false)}
						dontShowAnymore={handleDontShowAnymore}
						isChecked={additional.dontShowAnymore!}
					/>
				</>
			)}
		</nav>
	);
};

export default ClientStoreBreadcrumbs;
